import { ListBox } from "primereact/listbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import TruncatedTitle from "../../general/TruncatedTitle";
import Utils from "util/Utils";
import {
  ExecutionResultDTO,
  JobInfoDTO,
  ResultStatus,
} from "../../../serviceapi/jobRunnerApi";

interface JobRunnerListBoxProps {
  listLoad: boolean;
  jobs: JobInfoDTO[];
  selectedJob: JobInfoDTO | null;
  lastExecutions: Record<string, ExecutionResultDTO>;
  handleJobSelection: (job: any) => void;
  openJobLogs: (job: JobInfoDTO) => void;
}

type JobStatus = "off" | "success" | "failed" | "unknown";

const statusIconConfig: Record<
  JobStatus,
  { icon: string; color: string; label: string }
> = {
  off: { icon: "pi-power-off", color: "#ffa2a2", label: "Turned off" },
  success: { icon: "pi-check-circle", color: "#AAFF00", label: "Success" },
  failed: { icon: "pi-times-circle", color: "#ff5f5f", label: "Failed" },
  unknown: { icon: "pi-minus-circle", color: "gray", label: "Not executed" },
};

const getJobStatus = (
  job: JobInfoDTO,
  lastExecution?: ExecutionResultDTO
): JobStatus => {
  if (job.enabled === false) return "off";
  if (!lastExecution) return "unknown";
  return lastExecution.status === ResultStatus.Success ? "success" : "failed";
};

const JobRunnerListBox = ({
  listLoad,
  jobs,
  selectedJob,
  lastExecutions,
  handleJobSelection,
  openJobLogs,
}: JobRunnerListBoxProps) => {
  const listBoxItemTemplate = (option: JobInfoDTO) => {
    const lastExecution = lastExecutions[option.taskId];
    const { icon, color, label } =
      statusIconConfig[getJobStatus(option, lastExecution)];
    const tooltip = lastExecution
      ? `${label} — Last executed: ${Utils.formatISOToBasicDateFormat(
          lastExecution.startTime
        )}`
      : `${label} — Never executed`;

    return (
      <span className="flex flex-row align-items-center gap-2" style={{ marginLeft: "-6px"}}>
        <i
          className={`pi ${icon} job-status-icon py-2`}
          style={{ color, flexShrink: 0, cursor: "pointer" }}
          data-pr-tooltip={tooltip}
          onMouseDown={() => openJobLogs(option)}
        />
        <TruncatedTitle title={option.taskTitle ?? option.taskId} />
      </span>
    );
  };

  return (
    <div className="left-side">
      <div className="list-box">
        {listLoad ? (
          <div className="list-box-loading">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            <Tooltip target=".job-status-icon" position="bottom" />
            <ListBox
              value={selectedJob}
              options={jobs}
              onChange={(e) => handleJobSelection(e.value)}
              filter
              filterPlaceholder="Search"
              filterBy="taskId,taskTitle"
              optionLabel="name"
              itemTemplate={listBoxItemTemplate}
              style={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
              }}
              listStyle={{ height: "calc(100% - 36px)" }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default JobRunnerListBox;
